import { LucideIcon } from 'components/LucideIcon'
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from 'designSystem/components/DropdownMenu'
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from 'designSystem/components/Sidebar'
import { StyledDropdownMenuTrigger } from 'features/navbar/NavbarDropdown'
import { PermissionGateToggle } from 'features/navbar/PermissionGateToggle'
import { UnitsToggle } from 'features/navbar/UnitsToggle'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import styled from 'styled-components'
import {
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
  PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

import { ProfileAndSettingsBase } from './ProfileAndSettingsBase'
import { type ProfileAndSettingsProps } from './useProfileAndSettingsState'

const SidebarProfileAvatar = styled.span`
  position: relative;
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.activeText};

  &::before {
    content: '';
    position: absolute;
    width: 1.5rem;
    height: 1.5rem;
    border: 1px solid ${({ theme }) => theme.forecast.border};
    border-radius: var(--radius);
  }

  svg {
    position: relative;
    width: 1.125rem;
    height: 1.125rem;
  }
`

const SidebarProfileName = styled.span`
  transition: opacity 0.15s ease;

  [data-collapsible='icon'] & {
    opacity: 0;
  }
`

const SidebarProfileMenuContent = styled(DropdownMenuContent)`
  width: 15rem;
  border-radius: 0.75rem;
  padding: 0.5rem;
`

const SidebarProfileMenuItem = styled(DropdownMenuItem)`
  padding: 0.5rem;
  font-size: 0.75rem;
`

const SidebarProfileMenuDestructiveItem = styled(SidebarProfileMenuItem)`
  color: var(--destructive);

  &:focus {
    color: var(--destructive);
  }
`

export const ProfileAndSettingsSidebar: React.FC<ProfileAndSettingsProps> = ({
  onOpenUploadDocuments,
}) => (
  <ProfileAndSettingsBase
    renderTrigger={(state) => (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={state.isModalOpen}
          tooltip={state.userDisplayName}
          variant='embedded'>
          <StyledDropdownMenuTrigger
            type='button'
            aria-label={`Open profile settings menu for ${state.userDisplayName}`}>
            <SidebarProfileAvatar>
              <LucideIcon name='User' />
            </SidebarProfileAvatar>
            <SidebarProfileName>{state.userDisplayName}</SidebarProfileName>
          </StyledDropdownMenuTrigger>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )}
    renderContent={(state) => (
      <SidebarProfileMenuContent
        side='top'
        sideOffset={4}
        align='start'
        width='auto'>
        {state.shouldShowUnits && <UnitsToggle />}
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <PermissionGateToggle />
        </PermissionsGate>
        {onOpenUploadDocuments ? (
          <PermissionsGate
            requires={[
              PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_EDITOR,
              PERMISSION_CANTILEVER_POWER_DOCUMENT_NAVIGATOR_ADMIN,
            ]}>
            <SidebarProfileMenuItem onSelect={onOpenUploadDocuments}>
              Upload Documents
            </SidebarProfileMenuItem>
          </PermissionsGate>
        ) : null}
        <SidebarProfileMenuItem onSelect={() => state.setShowToSModal(true)}>
          Terms of Service
        </SidebarProfileMenuItem>
        <SidebarProfileMenuItem asChild>
          <a href='mailto:support@geckorobotics.com'>Email Support</a>
        </SidebarProfileMenuItem>
        <SidebarProfileMenuDestructiveItem onSelect={state.handleLogOut}>
          Log out
        </SidebarProfileMenuDestructiveItem>
      </SidebarProfileMenuContent>
    )}
  />
)
